import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router'
import { lazy, Suspense } from 'react'
import { useAuth } from './auth/authContext'
import { RequireAuth } from './auth/RequireAuth'
import { Welcome } from './auth/Welcome'
import { SignedOut } from './auth/SignedOut'
import { ProfilePage } from './features/profile/ProfilePage'
import { NotFound } from './components/NotFound'
import { PrimaryShell } from './components/PrimaryShell'
import { StandaloneShell } from './components/StandaloneShell'
import { SplashGate } from './components/SplashGate'
import { HomeScreen } from './screens/home/HomeScreen'
import { ProfileScreen } from './screens/profile/ProfileScreen'
import { ApplicationStatusScreen } from './screens/application-status/ApplicationStatusScreen'
import { ApplicationOfInterestScreen } from './screens/application-of-interest/ApplicationOfInterestScreen'
import { NdaScreen } from './screens/nda/NdaScreen'

// Lazy: pulls the form.io renderer (+ its CSS) into a separate chunk so it never
// touches the main bundle — only loads when a form route is visited.
const MeetingsPage = lazy(() => import('./features/meetings/MeetingsPage'))
const meetingsEnabled = import.meta.env.VITE_MEETINGS_ENABLED === 'true'

const FormPage = lazy(() => import('./features/forms/FormPage').then((m) => ({ default: m.FormPage })))
const ApplicationFormPage = lazy(() =>
  import('./features/forms/ApplicationFormPage').then((m) => ({ default: m.ApplicationFormPage })),
)

// Dev-only. In prod, import.meta.env.DEV is statically `false`, so this
// branch + the dynamic import are tree-shaken out of the bundle entirely.
const DevIndex = import.meta.env.DEV ? lazy(() => import('./dev/DevIndex')) : null
const Tokens = import.meta.env.DEV ? lazy(() => import('./dev/Tokens')) : null
const ComponentGallery = import.meta.env.DEV
  ? lazy(() => import('./dev/ComponentGallery'))
  : null

/** Auth gate for standalone screens (no tab bar) — redirects to Keycloak when signed out. */
function Protected() {
  return (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  )
}

export function App() {
  // Splash holds until Keycloak's check-sso resolves; then the `/` → `/home`
  // redirect either shows the tab shell (authenticated) or, via RequireAuth,
  // hands off to the Keycloak hosted login.
  const { initialized } = useAuth()

  return (
    <BrowserRouter>
      <SplashGate ready={initialized}>
        <Routes>
          {/* Public entry. `/` triggers the cold-launch splash; the mock login is
              deprecated, so both `/` and `/login` redirect into the authed area. */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/login" element={<Navigate to="/home" replace />} />
          {/* Public signed-out landing — logout redirects here (see AuthProvider). */}
          <Route path="/signed-out" element={<SignedOut />} />
          {/* Invite-email onboarding entry — starts Keycloak login. */}
          <Route path="/welcome" element={<Welcome />} />

          {/* Meetings are available to authenticated users in both primary shells when enabled. */}
          <Route element={<RequireAuth><PrimaryShell /></RequireAuth>}>
            <Route path="/home" element={<HomeScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            {meetingsEnabled && <>
              <Route path="/training" element={<Suspense fallback={<p>Loading meetings…</p>}><MeetingsPage /></Suspense>} />
              <Route path="/calendar" element={<Suspense fallback={<p>Loading calendar…</p>}><MeetingsPage agenda /></Suspense>} />
            </>}
          </Route>

          {/* Authenticated standalone screens (own header/back bar, no tab bar). */}
          <Route element={<Protected />}>
            {meetingsEnabled && <Route path="/meetings/sessions/:sessionId" element={<Suspense fallback={<p>Loading meeting…</p>}><MeetingsPage /></Suspense>} />}
            <Route path="/application-status" element={<ApplicationStatusScreen />} />
            <Route path="/application-of-interest" element={<ApplicationOfInterestScreen />} />
            {/* Renders its own website header + back bar inline, like the two
                siblings above — so it stays outside StandaloneShell. */}
            <Route path="/nda" element={<NdaScreen />} />
            {/* Form/profile drill-ins share StandaloneShell (header + back row) so they
                aren't dead-ends in installed-PWA standalone mode (no browser back).
                The shell owns the chrome — these screens render body only. */}
            {/* EDA KYC application form — schema fetched via the BFF. Titled back
                row, and a fixed /home target since it's reachable by deep link. */}
            <Route element={<StandaloneShell backLabel="application.title" backTo="/home" />}>
              <Route path="/apply" element={<Suspense fallback={null}><ApplicationFormPage /></Suspense>} />
            </Route>
            <Route element={<StandaloneShell />}>
              {/* Generic direct-render form.io route (by alias). */}
              <Route path="/app/forms/:formId" element={<Suspense fallback={null}><FormPage /></Suspense>} />
              {/* Token claims + BFF /me/asset-data (kept; folds into the Profile tab later). */}
              <Route path="/app" element={<ProfilePage />} />
            </Route>
          </Route>

          {import.meta.env.DEV && DevIndex && Tokens && ComponentGallery && (
            <>
              <Route path="/dev" element={<Suspense fallback={null}><DevIndex /></Suspense>} />
              <Route path="/dev/tokens" element={<Suspense fallback={null}><Tokens /></Suspense>} />
              <Route path="/dev/components" element={<Suspense fallback={null}><ComponentGallery /></Suspense>} />
            </>
          )}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SplashGate>
    </BrowserRouter>
  )
}
